import React, { useEffect, useState } from 'react';
import { withAnalyticsContext } from '@atlaskit/analytics-next';
import { SpotlightTransition } from '@atlaskit/onboarding';
import { token } from '@atlaskit/tokens';
import { ScheduleSpotlights } from '@atlassian/eoc-on-call';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { JiraSpotlight } from '@atlassian/jira-spotlight/src/ui/jira-spotlight.tsx';
import { commonMessages } from '../../../common/messages.tsx';
import { messages } from './messages.tsx';

type Props = {
	// eslint-disable-next-line jira/react/handler-naming
	stop?: () => void;
};

const EditRotationTour = ({ stop }: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [activeSpotlight, setActiveSpotlight] = useState<null | number>(null);
	const start = () => setActiveSpotlight(0);
	const next = () => setActiveSpotlight((activeSpotlight || 0) + 1);
	const back = () => setActiveSpotlight((activeSpotlight || 1) - 1);
	const end = () => setActiveSpotlight(null);

	useEffect(() => {
		start();
	}, []);

	const dismiss = () => {
		end();

		if (stop) {
			stop();
		}

		const event = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'dismissButton',
		});
		fireUIAnalytics(event);
	};

	const complete = () => {
		end();

		const event = createAnalyticsEvent({
			actionSubject: 'okButton',
			action: 'clicked',
		});
		fireUIAnalytics(event);
	};

	const renderActiveSpotlight = () => {
		const spotlights = [
			<JiraSpotlight
				actionsBeforeElement="1/2"
				actions={[
					{ onClick: next, text: formatMessage(commonMessages.next) },
					{
						onClick: dismiss,
						text: formatMessage(commonMessages.dismiss),
						appearance: 'subtle',
					},
				]}
				heading={formatMessage(messages.manageParticipantsTimings)}
				target={ScheduleSpotlights.editRotationForm}
				key={ScheduleSpotlights.editRotationForm}
				dialogPlacement="right top"
				targetRadius={3}
				targetBgColor={token('elevation.surface')}
				messageId="eoc-on-call.ui.tours.edit-rotation.jira-spotlight"
				messageType="engagement"
			>
				{formatMessage(messages.shiftParticipantsWillRotate)}
			</JiraSpotlight>,
			<JiraSpotlight
				actionsBeforeElement="2/2"
				actions={[
					{ onClick: complete, text: formatMessage(commonMessages.ok) },
					{
						onClick: back,
						text: formatMessage(commonMessages.back),
						appearance: 'subtle',
					},
				]}
				heading={formatMessage(messages.previewScheduleChanges)}
				target={ScheduleSpotlights.editModeSchedule}
				key={ScheduleSpotlights.editModeSchedule}
				targetRadius={3}
				dialogPlacement="right top"
				targetBgColor={token('elevation.surface')}
				messageId="eoc-on-call.ui.tours.edit-rotation.jira-spotlight.1"
				messageType="engagement"
			>
				{formatMessage(messages.previewAllowsToSeeChanges)}
			</JiraSpotlight>,
		];

		if (activeSpotlight === null) {
			return null;
		}

		return spotlights[activeSpotlight];
	};

	return <SpotlightTransition>{renderActiveSpotlight()}</SpotlightTransition>;
};

export default withAnalyticsContext({
	source: 'OpsEditRotationSpotlightModal',
	tags: ['spotlight', 'edit-rotation-spotlight'],
})(EditRotationTour);
